import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, ShieldCheck, Lock, ListChecks, Quote, Gift, Briefcase, HeartHandshake } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Motion";
import SectionHeader from "@/components/SectionHeader";
import Marquee from "@/components/Marquee";
import CTABanner from "@/components/CTABanner";
import BookButton from "@/components/BookButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { IMAGES } from "@/data/content";
import { useLang } from "@/i18n";
import { SEO } from "@/components/SEO";

const EXT_ICONS = [ShieldCheck, Lock, ListChecks];

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden bg-obsidian">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img src={IMAGES.hero} alt="" className="h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/80 via-obsidian/60 to-obsidian" />
      </motion.div>
      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-32 pt-40 sm:px-12 lg:px-20">
        <MaskedLine delay={0.1}>
          <span className="overline text-gold">{t("home.heroOverline")}</span>
        </MaskedLine>
        <h1 className="mt-8 max-w-4xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.25}>{t("home.heroLine1")}</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="italic text-gold">{t("home.heroLine2")}</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6}>
          <span className="mt-8 block max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            {t("home.heroSub")}
          </span>
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <BookButton testid="hero-book-consultation-button" />
          <Link to="/services" data-testid="hero-view-services-button" className="btn-outline-light">
            {t("common.viewServices")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
          </Link>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-20 grid max-w-3xl grid-cols-1 gap-6 border-t border-white/10 pt-8 sm:grid-cols-3"
        >
          {t("home.heroStats").map(({ stat, label }, i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <p className="font-mono text-sm uppercase tracking-[0.2em] text-gold">{stat}</p>
              <p className="mt-2 text-sm text-slate-400">{label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

const Extension = () => {
  const { t } = useLang();
  return (
    <section data-testid="extension-section" className="bg-alabaster py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 sm:px-12 lg:grid-cols-2 lg:gap-20 lg:px-20">
        <Reveal>
          <div className="relative">
            <div className="absolute -left-4 -top-4 h-full w-full rounded-xl border border-gold/40" />
            <img
              src={IMAGES.bright}
              alt="Bright, minimal executive workspace by a window"
              loading="lazy"
              className="relative aspect-[4/3] w-full rounded-xl object-cover shadow-2xl"
            />
          </div>
        </Reveal>
        <div>
          <SectionHeader
            testid="extension"
            overline={t("home.extOverline")}
            title={t("home.extTitle")}
            copy={t("home.extCopy")}
          />
          <div className="mt-10 space-y-5">
            {t("home.extPoints").map((point, i) => {
              const Icon = EXT_ICONS[i];
              return (
                <Reveal key={i} delay={0.1 + i * 0.1} className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold/30 bg-white text-gold-muted">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="font-semibold text-obsidian">{point.title}</p>
                    <p className="mt-1 text-sm text-slate-600">{point.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const ServicesPreview = () => {
  const { t, c } = useLang();
  return (
    <section data-testid="services-preview-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHeader
            testid="services-preview"
            overline={t("home.svcOverline")}
            title={t("home.svcTitle")}
            copy={t("home.svcCopy")}
          />
          <Reveal delay={0.15}>
            <Link to="/services" data-testid="services-preview-view-all-link" className="btn-outline-dark">
              {t("home.svcViewAll")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {c.services.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 3) * 0.1}>
              <Link
                to="/services"
                data-testid={`service-preview-card-${s.slug}`}
                className="card-lift group block h-full rounded-xl border border-obsidian/10 bg-alabaster p-8 hover:border-gold/50 hover:shadow-xl"
              >
                <p className="font-mono text-xs tracking-[0.25em] text-gold-muted">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-4 font-serif text-2xl leading-snug text-obsidian">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-500">{s.tagline}</p>
                <p className="mt-3 font-mono text-xs tracking-wide text-gold-muted">{s.price}</p>
                <ul className="mt-6 space-y-2.5">
                  {s.featured.slice(0, 4).map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-muted" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 flex items-center gap-2 text-sm font-semibold text-gold-muted transition-colors group-hover:text-gold">
                  {t("home.svcViewDetails")} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1" />
                </p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const SplitGateway = () => {
  const { t } = useLang();
  const cards = [
    {
      icon: Briefcase,
      title: t("home.splitBizTitle"),
      text: t("home.splitBizText"),
      points: t("home.splitBizPoints"),
      cta: t("home.splitBizCta"),
      to: "/services",
      testid: "business",
      dark: false,
    },
    {
      icon: HeartHandshake,
      title: t("home.splitPerTitle"),
      text: t("home.splitPerText"),
      points: t("home.splitPerPoints"),
      cta: t("home.splitPerCta"),
      to: "/personal-support",
      testid: "personal",
      dark: true,
    },
  ];
  return (
    <section data-testid="split-gateway-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader
          center
          testid="split-gateway"
          overline={t("home.splitOverline")}
          title={t("home.splitTitle")}
          copy={t("home.splitCopy")}
        />
        <div className="mt-16 grid gap-8 lg:grid-cols-2">
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <Reveal key={card.testid} delay={i * 0.12}>
                <div
                  data-testid={`split-card-${card.testid}`}
                  className={`card-lift flex h-full flex-col rounded-2xl p-10 ${
                    card.dark
                      ? "border border-gold/40 bg-obsidian shadow-2xl"
                      : "border border-obsidian/10 bg-alabaster hover:border-gold/50 hover:shadow-xl"
                  }`}
                >
                  <span
                    className={`flex h-12 w-12 items-center justify-center rounded-lg border ${
                      card.dark ? "border-gold/40 bg-obsidian-card text-gold" : "border-gold/30 bg-white text-gold-muted"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className={`mt-6 font-serif text-3xl leading-snug ${card.dark ? "text-white" : "text-obsidian"}`}>
                    {card.title}
                  </h3>
                  <p className={`mt-3 text-sm leading-relaxed ${card.dark ? "text-slate-400" : "text-slate-600"}`}>
                    {card.text}
                  </p>
                  <ul className="mt-7 space-y-2.5">
                    {card.points.map((point) => (
                      <li
                        key={point}
                        className={`flex items-start gap-2.5 text-sm ${card.dark ? "text-slate-300" : "text-slate-600"}`}
                      >
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-muted" />
                        {point}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={card.to}
                    data-testid={`split-${card.testid}-cta`}
                    className={`mt-auto pt-8 flex items-center gap-2 text-sm font-semibold transition-colors ${
                      card.dark ? "text-gold hover:text-gold-bright" : "text-gold-muted hover:text-gold"
                    }`}
                  >
                    {card.cta}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100" />
                  </Link>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};


const HowItWorks = () => {
  const { t, c } = useLang();
  return (
    <section data-testid="how-it-works-section" className="bg-obsidian py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader dark testid="how-it-works" overline={t("home.stepsOverline")} title={t("home.stepsTitle")} />
        <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {c.steps.map((step, i) => (
            <Reveal key={step.num} delay={i * 0.12} className="relative">
              <div data-testid={`step-card-${i + 1}`} className="border-t border-white/10 pt-8">
                <p className="font-mono text-5xl font-light text-gold/50">{step.num}</p>
                <h3 className="mt-5 font-serif text-2xl text-white">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{step.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhyChoose = () => {
  const { t, c } = useLang();
  return (
    <section data-testid="why-choose-section" className="bg-alabaster py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader
          testid="why-choose"
          overline={t("home.whyOverline")}
          title={t("home.whyTitle")}
          copy={t("home.whyCopy")}
        />
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {c.why.map(({ icon: Icon, title, text }, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08}>
              <div
                data-testid={`why-card-${i + 1}`}
                className="card-lift h-full rounded-xl border border-obsidian/10 bg-white p-7 hover:border-gold/50 hover:shadow-lg"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-gold/30 bg-alabaster text-gold-muted">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-base font-semibold text-obsidian">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const WhoWeWorkWith = () => {
  const { t, c } = useLang();
  return (
    <section data-testid="who-we-work-with-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader center testid="who-we-work-with" overline={t("home.whoOverline")} title={t("home.whoTitle")} />
        <Reveal delay={0.15} className="mt-14 flex flex-wrap justify-center gap-4">
          {c.clients.map((type, i) => (
            <span
              key={i}
              data-testid={`client-type-${i + 1}`}
              className="rounded-full border border-obsidian/15 bg-alabaster px-6 py-3 text-sm text-obsidian transition-colors duration-300 hover:border-gold hover:text-gold-muted"
            >
              {type}
            </span>
          ))}
        </Reveal>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { t, c } = useLang();
  const list = c.testimonials || [];
  return (
    <section data-testid="testimonials-section" className="bg-alabaster py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeader center testid="testimonials" overline={t("home.testOverline")} title={t("home.testTitle")} />
        {list.length === 0 ? (
          <Reveal>
            <div
              data-testid="testimonials-launch-note"
              className="mx-auto mt-16 max-w-2xl rounded-2xl border border-gold/30 bg-gradient-to-r from-gold/10 to-transparent p-10 text-center"
            >
              <Quote className="mx-auto h-6 w-6 text-gold" />
              <p className="mt-5 font-serif text-xl italic leading-relaxed text-obsidian">
                {t("home.testLaunch")}
              </p>
            </div>
          </Reveal>
        ) : (
          <div className={`mt-16 grid gap-6 ${list.length < 3 ? "md:grid-cols-1" : "md:grid-cols-3"}`}>
            {list.map((tm, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <figure
                  data-testid={`testimonial-card-${i + 1}`}
                  className="card-lift mx-auto flex h-full max-w-3xl flex-col rounded-xl border border-obsidian/10 bg-white p-10 hover:border-gold/50 hover:shadow-lg"
                >
                  <Quote className="h-6 w-6 text-gold" />
                  <blockquote className="mt-5 flex-1 font-serif text-lg italic leading-relaxed text-obsidian">
                    {tm.quote.split("\n\n").map((p) => (
                      <p key={p.slice(0, 30)} className="mb-4 last:mb-0">
                        {p}
                      </p>
                    ))}
                  </blockquote>
                  <figcaption className="mt-6 border-t border-obsidian/10 pt-5">
                    <p className="text-sm font-semibold not-italic text-obsidian">{tm.name}</p>
                    <p className="mt-1 font-mono text-xs tracking-wide text-slate-500">{tm.detail}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const LoyaltyTeaser = () => {
  const { t } = useLang();
  return (
    <section data-testid="loyalty-teaser-section" className="bg-obsidian px-6 pb-0 sm:px-12 lg:px-20">
      <Reveal>
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 rounded-2xl border border-gold/30 bg-gradient-to-r from-gold/10 to-transparent p-8">
          <div className="flex items-start gap-4">
            <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gold/40 bg-obsidian-card text-gold">
              <Gift className="h-4 w-4" />
            </span>
            <p className="max-w-2xl font-serif text-xl leading-relaxed text-white sm:text-2xl">
              {t("home.loyaltyText")}
            </p>
          </div>
          <Link to="/packages" data-testid="loyalty-teaser-link" className="btn-gold !py-3">
            {t("home.loyaltyCta")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
          </Link>
        </div>
      </Reveal>
    </section>
  );
};

const PackagesTeaser = () => {
  const { t, c } = useLang();
  return (
    <section data-testid="packages-teaser-section" className="bg-obsidian py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHeader
            dark
            testid="packages-teaser"
            overline={t("home.packOverline")}
            title={t("home.packTitle")}
            copy={t("home.packCopy")}
          />
          <Reveal delay={0.15}>
            <Link to="/packages" data-testid="packages-teaser-view-all-link" className="btn-outline-light">
              {t("home.packViewAll")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {c.plans.slice(0, 3).map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.1}>
              <div
                data-testid={`package-teaser-${p.slug}`}
                className="card-lift h-full rounded-xl border border-white/10 bg-obsidian-card p-8 hover:border-gold/40"
              >
                <p className="font-mono text-sm tracking-wide text-gold">{p.price}</p>
                <h3 className="mt-4 font-serif text-2xl text-white">{p.name}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.fit}</p>
                <Link
                  to="/packages"
                  data-testid={`package-teaser-link-${p.slug}`}
                  className="mt-6 flex items-center gap-2 text-sm font-semibold text-gold transition-colors hover:text-gold-bright"
                >
                  {t("home.packIncluded")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-6">
          <div
            data-testid="package-teaser-global-priority"
            className="flex flex-wrap items-center justify-between gap-6 rounded-xl border border-gold/25 bg-gradient-to-r from-gold/10 to-transparent p-8"
          >
            <div>
              <h3 className="font-serif text-2xl text-white">{t("home.packGlobalTitle")}</h3>
              <p className="mt-2 max-w-xl text-sm text-slate-400">{t("home.packGlobalText")}</p>
            </div>
            <Link to="/contact?plan=global-priority" data-testid="package-teaser-global-quote-button" className="btn-gold !py-3">
              {t("home.packGlobalBtn")}
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const FaqPreview = () => {
  const { t, c } = useLang();
  return (
    <section data-testid="faq-preview-section" className="bg-alabaster py-24 lg:py-32">
      <div className="mx-auto max-w-4xl px-6 sm:px-12">
        <SectionHeader center testid="faq-preview" overline={t("home.faqOverline")} title={t("home.faqTitle")} />
        <Reveal delay={0.15} className="mt-14">
          <Accordion type="single" collapsible className="w-full">
            {c.faqs.slice(0, 5).map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`home-faq-item-${i + 1}`} className="border-obsidian/10">
                <AccordionTrigger
                  data-testid={`home-faq-trigger-${i + 1}`}
                  className="text-left font-serif text-lg text-obsidian hover:text-gold-muted hover:no-underline"
                >
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-600">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
        <Reveal delay={0.2} className="mt-10 text-center">
          <Link to="/faq" data-testid="faq-preview-view-all-link" className="btn-outline-dark">
            {t("home.faqViewAll")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        title="VCK Business Lab — Elevated Virtual Support for Modern Business"
        description="Professional virtual administrative and business support — business & executive support, translation, multilingual client support, website building, and 24/7 priority assistance. Delivered 100% virtually worldwide."
        path="/"
      />
      <Hero />
      <SplitGateway />
      <Extension />
      <ServicesPreview />
      <Marquee />
      <HowItWorks />
      <WhyChoose />
      <WhoWeWorkWith />
      <Testimonials />
      <LoyaltyTeaser />
      <PackagesTeaser />
      <FaqPreview />
      <CTABanner />
    </div>
  );
}
